import { NgModule } from '@angular/core';

// modules (angular)
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// modules (third-party)
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ModalModule } from 'ngx-bootstrap/modal';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {TypeaheadModule} from 'ngx-bootstrap/typeahead';  
import { RedZoomModule } from 'ngx-red-zoom';

// directives
import { ClickDirective } from './directives/click.directive';
import { CollapseContentDirective, CollapseDirective, CollapseItemDirective } from './directives/collapse.directive';
import { DepartmentsAreaDirective } from './directives/departments-area.directive';
import { DropdownDirective } from './directives/dropdown.directive';
import { FakeSlidesDirective } from './directives/fake-slides.directive';
import { OutsideTouchClickDirective } from './directives/outside-touch-click.directive';
import { OwlPreventClickDirective } from './directives/owl-prevent-click.directive';
import { TouchClickDirective } from './directives/touch-click.directive';
import { TouchSlopDirective } from './directives/touch-slop.directive';
import { DecimalTwoDirective } from './directives/decimal-two.directive';  

// components
import { AlertComponent } from './components/alert/alert.component';
import { LeadFormComponent } from './components/lead-form/lead-form.component';
import { TalkDialogComponent } from './components/talk-dialog/talk-dialog.component';
import { IconComponent } from './components/icon/icon.component';
import { InputNumberComponent } from './components/input-number/input-number.component';
import { LoadingBarComponent } from './components/loading-bar/loading-bar.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { PostCardComponent } from './components/post-card/post-card.component';
import { ProductCardComponent } from './components/product-card/product-card.component';
import { ProductComponent } from './components/product/product.component';
import { QuickviewComponent } from './components/quickview/quickview.component';
import { RatingComponent } from './components/rating/rating.component';
import { SearchComponent } from './components/search/search.component';
import { ShareButtonsComponent } from './components/share-buttons/share-buttons.component';
import { SocialLinksComponent } from './components/social-links/social-links.component';
import { OrderStatusPanelComponent } from './components/order-status-panel/order-status-panel.component';
import { PaymentMarksComponent } from './components/payment-marks/payment-marks.component';
import { OrderProgressComponent } from './components/order-progress/order-progress.component';
import { ParcelStepsComponent } from './components/parcel-steps/parcel-steps.component';
import { StockAlertComponent } from './components/stock-alert/stock-alert.component';

// pipes
import { AbsoluteUrlPipe } from './pipes/absolute-url.pipe';
import { ColorTypePipe } from './pipes/color-type.pipe';
import { CountryFlagPipe } from './pipes/country-flag.pipe';
import { ChargedLinePipe, ChargedMoneyPipe, CurrencyFormatPipe, CurrencyLinePipe, LocalMoneyPipe, QuotedMoneyPipe, SiteCurrencyPipe } from './pipes/currency-format.pipe';
import { OwnerCurrencyNoteComponent } from './components/owner-currency-note/owner-currency-note.component';
import { CardPricePipe } from './pipes/card-price.pipe';
import { SoldByPipe } from './pipes/sold-by.pipe';
import { ProductNamePipe } from './pipes/product-name.pipe';
import { StoredMoneyPipe } from './pipes/stored-money.pipe';
import { SiteSignPipe } from './pipes/site-sign.pipe';
import { SellerStatementComponent } from './components/seller-statement/seller-statement.component';
import { ProductGalleryComponent } from './components/product-gallery/product-gallery.component';  
//import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
 
//import { AdBannerComponent } from './components/ad-banner/ad-banner.component';
import { ShopAndFeaturingRequestFormComponent } from './components/shop-and-featuring-request-form/enlisting-request-form.component';  
import { DragDropModule } from '@angular/cdk/drag-drop';
// UI languages: the t, tHtml and localizeLink pipes, the language switch and its suggestion (shared/culture).
import { CultureModule } from './culture/culture.module';
import { ConfirmActionComponent } from './components/confirm-action/confirm-action.component';
import { BrandLogoComponent } from './components/brand-logo/brand-logo.component';
import { NoLeadingZeroDirective } from './directives/no-leading-zero.directive';
import { RouterLinksDirective } from './directives/router-links.directive';
import { BrandMarkPipe, SiteHtmlPipe, BrandTextPipe } from './pipes/site-html.pipe';
import { AppDialogHostComponent } from './dialogs/app-dialog';
import { SiteFaqComponent } from './components/site-faq/site-faq.component';
// a seller's shop as a card: the shops directory (ShopsDirectoryModule) and the home page's "Shops to follow" (AppModule)
import { ShopCardComponent } from './components/shop-card/shop-card.component';


@NgModule({
    declarations: [
        AppDialogHostComponent,
        // the seller forms (SellerLeads) and Talk to us's pop-up
        LeadFormComponent,
        TalkDialogComponent,
        // directives
        ClickDirective,
        CollapseContentDirective,
        CollapseDirective,
        CollapseItemDirective,
        DepartmentsAreaDirective,
        DropdownDirective,
        FakeSlidesDirective,
        OutsideTouchClickDirective,
        OwlPreventClickDirective,
        TouchClickDirective,
        TouchSlopDirective,
        // components
        AlertComponent,
        IconComponent,
        InputNumberComponent,
        LoadingBarComponent,
        PageHeaderComponent,
        PaginationComponent,
        BrandLogoComponent,
        PostCardComponent,
        ProductCardComponent,
        ProductComponent,
        QuickviewComponent,
        RatingComponent,
        SearchComponent,
        ShareButtonsComponent,
        SocialLinksComponent, 
        OrderStatusPanelComponent,
        PaymentMarksComponent,
        OrderProgressComponent,
        ParcelStepsComponent,
        StockAlertComponent,
        OwnerCurrencyNoteComponent,
        SellerStatementComponent,
        // pipes
        AbsoluteUrlPipe,
        ColorTypePipe,
        CountryFlagPipe,
        CurrencyFormatPipe,
        SiteCurrencyPipe,
        QuotedMoneyPipe,
        LocalMoneyPipe,
        CurrencyLinePipe,
        ChargedMoneyPipe,
        ChargedLinePipe,
        CardPricePipe,
        SoldByPipe,
        ProductNamePipe,
        StoredMoneyPipe,
        SiteSignPipe,
        ProductGalleryComponent,
      //  AdBannerComponent,
        ShopAndFeaturingRequestFormComponent,
        ConfirmActionComponent,
        NoLeadingZeroDirective,
        DecimalTwoDirective,
        // the site's own pages (SitePage)
        RouterLinksDirective,
        SiteHtmlPipe,
        BrandMarkPipe,
        BrandTextPipe,
        SiteFaqComponent,
        // sellers' shops
        ShopCardComponent
    ],
    imports: [
        // modules (angular)
        CommonModule, 
        FormsModule,
        // No HttpClientModule here: imported by a lazily loaded module it made an HttpClient of that module's own,
        // WITHOUT the app's AuthInterceptor - a signed-in call from it went out with no token (Payment/CardSession,
        // 401, 2026-09-26). AppModule imports it once, for the whole app.
        ReactiveFormsModule,
        RouterModule,
        // modules (third-party)
        CarouselModule,
        ModalModule.forRoot(),
        TooltipModule.forRoot(),   
        TypeaheadModule.forRoot(),
        RedZoomModule,
        DragDropModule,
        CultureModule
    ],
    exports: [
        AppDialogHostComponent,
        LeadFormComponent,
        TalkDialogComponent,
        CultureModule,
        // directives
        ClickDirective,
        CollapseContentDirective,
        CollapseDirective,
        CollapseItemDirective,
        DepartmentsAreaDirective,
        DropdownDirective,
        FakeSlidesDirective,
        OutsideTouchClickDirective,
        OwlPreventClickDirective,
        TouchClickDirective,
        TouchSlopDirective,
        // components
        AlertComponent,
        IconComponent,
        InputNumberComponent,
        LoadingBarComponent,
        PageHeaderComponent,
        PaginationComponent,
        BrandLogoComponent,
        PostCardComponent,
        ProductCardComponent,
        ProductComponent,
        QuickviewComponent,
        RatingComponent,
        SearchComponent, 
        SocialLinksComponent, 
        OrderStatusPanelComponent,
        PaymentMarksComponent,
        OrderProgressComponent,
        ParcelStepsComponent,
        StockAlertComponent,
        OwnerCurrencyNoteComponent,
        SellerStatementComponent,
        // pipes
        AbsoluteUrlPipe,
        ColorTypePipe,
        CountryFlagPipe,
        CurrencyFormatPipe,
        SiteCurrencyPipe,
        QuotedMoneyPipe,
        LocalMoneyPipe,
        CurrencyLinePipe,
        ChargedMoneyPipe,
        ChargedLinePipe,
        CardPricePipe,
        SoldByPipe,
        ProductNamePipe,
        StoredMoneyPipe,
        SiteSignPipe,
        ShareButtonsComponent,
       // AdBannerComponent,
        DragDropModule,
        // the seller application, on its own page (/sell/apply) as well as in the modal
        ShopAndFeaturingRequestFormComponent,
        // the site's own pages (SitePage)
        RouterLinksDirective,
        SiteHtmlPipe,
        BrandMarkPipe,
        BrandTextPipe,
        SiteFaqComponent,
        // sellers' shops
        ShopCardComponent
    ]
})
export class SharedModule { }
